export interface Authentication {
    id: number,
    type: string
}

export interface Place {
    id: string,
    name: string,
    city: string,
    region: string,
    maxGuests: number,
    supportedEventTypes: string[],
    budget: number,
    wpId: number,
    acceptReward: boolean,
    firstConnection: boolean,
    locHallPoints: number
}

export interface EventType {
    value: string,
    label: string
}

export const EVENT_TYPES: EventType[] = [
    {value: 'BREAKFAST', label: 'Petit déjeuner'},
    {value: 'MEETING', label: 'Réunion'},
    {value: 'CONFERENCE', label: 'Séminaire'},
    {value: 'SHOOTING', label: 'Shooting Photo / Tournage'},
    {value: 'DINNER', label: 'Dîner'},   
    {value: 'PARTY', label: 'Soirée'}
]

export const getEventFromValue = (value: string): EventType => {
    return EVENT_TYPES.find(eventType => eventType.value === value) || {value: '', label: ''}
}


export interface Region {
    value: string,
    label: string
}

export const REGIONS: Region[] = [
    { value:"auvergne-rhone-alpes", label:"Auvergne Rhone Alpes" },
    { value:"bourgogne-franche-comte", label:"Bourgogne Franche Compté" },
    { value:"bretagne", label:"Bretagne" },
    { value:"centre-val-de-loire", label:"Centre Val de Loire" },
    { value:"corse", label:"Corse" },
    { value:"grand-est", label:"Grand Est" },
    { value:"guadeloupe", label:"Guadeloupe" },
    { value:"guyane", label:"Guyane" },
    { value:"hauts-de-france", label:"Hauts de France" },
    { value:"ile-de-france", label:"Île de France" },
    { value:"la-reunion", label:"la Réunion" },
    { value:"martinique", label:"Martinique" },
    { value:"mayotte", label:"Mayotte" },
    { value:"normandie", label:"Normandie" },
    { value:"nouvelle-aquitaine", label:"Nouvelle Aquitaine" },
    { value:"occitanie", label:"Occitanie" },
    { value:"pays-de-la-loire", label:"Pays de la Loire" },
    { value:"provence-alpes-cote-dazur", label:"Provence Alpes côte d'Azur" },
    { value:"unknown", label:"non renseignée" }
]

export interface LeadCreation {
    type: string | null;
    targetDate: string;
    startTime: string;
    endTime: string;
    guests: number | string;
    budget: number | string;
    region: string | null;
    city: string;
    description: string;
    contactName: string;
    contactCompany: string;
    contactEmailAddress: string;
    contactMobilePhone: string;
    contactLandlinePhone: string;
}

export interface PublicLead extends LeadCreation {
    id: string;
    source: string;
    sourceId: string;
    status: string;
    availableSlots : number;
}

export interface Lead extends PublicLead {
    contactName: string;
    contactEmailAddress: string;
    contactMobilePhone: string;
    contactLandlinePhone: string;
}

export interface FollowedLead extends Lead {
    followerStatus: string;
}

export function isFollowedLead(obj: any): obj is FollowedLead {
    return typeof obj === "object" &&
    obj !== null &&
    typeof obj.followerStatus === "string";
}

export interface FollowerStatus {
    value: string,
    label: string,
    css: string
}
export const FOLLOWER_STATUS: FollowerStatus[] = [
    { value:'FOLLOWING', label:'Suivi', css:'bg-success text-white'},
    { value:'WON', label:'Brief gagné 🥳', css:'bg-success text-white'},
    { value:'LOST', label:'Brief perdu 😔', css:'bg-secondary text-white'},
]

export const getFollowerStatusValueToDisplay = (value: string): FollowerStatus => {
    return FOLLOWER_STATUS.find(status => status.value === value) || { value: '', label: '', css: ''}
}

export interface LeadWithBill extends FollowedLead {
    billAmount: number | string;
    sourceFee: number | string;
    locHallFee: number | string;
}

export interface LocHallBill {
    billAmount: number | string;
    sourceFee: number | string;
    locHallFee: number | string;
}

export enum NotificationType {
    information,
    error,
}

export interface Notification {
    type: NotificationType;
    message: string | null;
}

export interface NotificationContextType {
    notification: Notification | null;
    triggerNotification: (notification: Notification) => void;
    clearNotification: () => void;
}

export interface Administrator {
    id: string,
    name: string,
}


export interface LeadStatus {
    id: number,
    value: string,
    label: string,
    css: string
}
export const LEAD_STATUS: LeadStatus[] = [
    { id:1, value:'to_validate', label:'En attente de validation', css:'bg-warning'},
    { id:2, value:'refused', label:'Refusé', css:'bg-danger text-white'},
    { id:3, value:'available', label:'En cours', css:'bg-primary text-white'},
    { id:4, value:'followed', label:'En cours', css:'bg-primary text-white'},
    { id:5, value:'signed', label:'Signé', css:'bg-success text-white'},
    { id:6, value:'billed', label:'Facturé', css:'bg-success text-white'},
    { id:7, value:'verified', label:'Facture validée', css:'bg-success text-white'},
    { id:8, value:'archived', label:'archivé', css:'bg-secondary text-white'},
]

export const getLeadStatusFromValue = (value: string): LeadStatus => {
    return LEAD_STATUS.find(status => status.value === value) || {id : -1, value: '', label: '', css: ''}
}

export const isLeadSigned = (value: string): boolean => {
    const actualValue = getLeadStatusFromValue(value);
    return actualValue.value === "signed";
}

export const isLeadBilled = (value: string): boolean => {
    const actualValue = getLeadStatusFromValue(value);
    return actualValue.value === "billed";
}

export const isLeadVerified = (value: string): boolean => {
    const actualValue = getLeadStatusFromValue(value);
    return actualValue.value === "verified";
}

export interface PlaceDashboard {
    placeId: string,
    startDate: Date,
    endDate: Date,
    locHallPoints: number,
    sharedLeads: number,
    signedLeads: number,
    briefMarketSize: number,
    earnedFees: number,
}

export interface DateRange {
    value: string,
    label: string,
    startDate: Date | null,
    endDate: Date | null
  }

function getFirstDayOfMonth(): Date {
    const today = new Date();
    return new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0));
}

function getLastDayOfMonth(): Date {
    const today = new Date();
    return new Date(Date.UTC(today.getFullYear(), today.getMonth() +1, 0, 0, 0, 0));
}

function getFirstDayOfYear(): Date {
    const today = new Date();
    return new Date(Date.UTC(today.getFullYear(), 0, 1, 0, 0, 0));
}

function getLastDayOfYear(): Date {
    const today = new Date();
    return new Date(Date.UTC(today.getFullYear(), 11, 31, 0, 0, 0));
}

export  const AVAILABLE_RANGES: DateRange[] = [
    {value: "currentYear", label: "Année en cours", startDate: getFirstDayOfYear(), endDate: getLastDayOfYear()},
    {value: "currentMonth", label: "Mois en cours", startDate: getFirstDayOfMonth(), endDate: getLastDayOfMonth()}
  ];
